import uuid from "uuid/v4";

import {
  CreateResourceInput,
  ModelResourceConnection,
  ModelStringKeyConditionInput,
  QuerySearchArgs,
  QuerySearchUserResourcesArgs,
  Resource,
  SearchableResourceConnection,
  SearchableResourceConnectionNew,
  Workspace,
} from "@examspark/types";

import { GraphQLAPI } from "../client";
import { resource } from "../fragments";
import * as queries from "../queries";
import { incrementWorkspaceNumber } from "../queries/workspace";

export type Params<T> = Partial<{
  label: string;
  fields?: string;
  variables: T;
}>;

export const getNextWorkspaceNumber = async (workspaceId: string) => {
  const result = await GraphQLAPI.client.query<{
    incrementWorkspaceCounter: Workspace;
  }>({
    query: incrementWorkspaceNumber,
    variables: {
      input: {
        id: workspaceId,
        sk: "workspace",
      },
    },
  });

  if (!result.data?.incrementWorkspaceCounter.counter) {
    throw new Error("Unable to genereate new workspace number.");
  }

  return result.data?.incrementWorkspaceCounter.counter.toString();
};

export const createResource = async (
  params: Params<Partial<CreateResourceInput>>,
) => {
  const { label, fields, variables } = params || {};
  if (!variables) return;
  if (!variables.id) {
    variables.id = uuid();
  }

  if (!variables.schemaVersion) {
    variables.schemaVersion = "2020-01-01";
  }
  // @ts-expect-error bad typing
  if (variables.dimensions?.__typename) {
    // @ts-expect-error bad typing
    delete variables.dimensions.__typename;
  }
  // @ts-expect-error bad typing
  if (variables.file?.__typename) {
    // @ts-expect-error bad typing
    delete variables.file.__typename;
  }

  return GraphQLAPI.client.query<{ createResource: Resource }>({
    query: queries.resource.createResource({
      label,
      fields,
    }),
    variables: {
      input: variables,
    },
  });
};

export const createNumberedResource = async (data: Partial<Resource>) => {
  if (!data.workspaceId)
    throw new Error("Unable to get next workspace number.");

  if (!data.id) {
    data.id = uuid();
  }
  const nextNumber = await getNextWorkspaceNumber(data.workspaceId);
  data.idInc = nextNumber;

  if (!data.schemaVersion) {
    data.schemaVersion = "2020-01-01";
  }
  if (data.dimensions?.__typename) {
    delete data.dimensions.__typename;
  }
  if (data.file?.__typename) {
    delete data.file.__typename;
  }

  return GraphQLAPI.client.query<{ createResource: Resource }>({
    query: queries.resource.createResource({
      fields: `
        ${resource.id}
        ${resource.basic}
      `,
    }),
    variables: {
      input: data,
    },
  });
};

export const putResource = async (params: Params<Partial<Resource>>) => {
  if (!params.variables?.id || !params.variables?.sk) return;
  const getResult = await getResource({
    variables: {
      id: params.variables.id,
      sk: params.variables.sk,
    },
  });

  if (getResult.data?.getResource) {
    await updateResource(params.variables);
  } else {
    await createResource(params);
  }
};

export const deleteResource = async (item: Partial<Resource>) => {
  if (!item.workspaceId) throw new Error("Missing workspaceId.");

  return GraphQLAPI.client.query<{
    deleteResource: Resource;
  }>({
    query: queries.resource.deleteResource(),
    variables: {
      input: {
        id: item.id,
        sk: item.sk,
        workspaceId: item.workspaceId,
      },
    },
  });
};

export const updateResource = async (data: Partial<Resource>) => {
  return GraphQLAPI.client.query<{
    updateResource: Resource;
  }>({
    query: queries.resource.updateResource(),
    variables: {
      input: data,
    },
  });
};

export const getResource = async <T = Resource>(
  params?: Params<{
    id: string;
    sk: string;
  }>,
) => {
  const { label, fields, variables } = params || {};
  return GraphQLAPI.client.query<{
    getResource: T;
  }>({
    query: queries.resource.getResource({
      fields,
      label,
    }),
    variables,
  });
};

export const search = async (params?: Params<QuerySearchArgs>) => {
  return GraphQLAPI.client.query<{
    search: SearchableResourceConnectionNew;
  }>({
    query: queries.search.search(params),
    variables: params?.variables,
  });
};

export const searchUserResources = async (
  params?: Params<QuerySearchUserResourcesArgs>,
) => {
  return GraphQLAPI.client.query<{
    searchUserResources: SearchableResourceConnection;
  }>({
    query: queries.resource.searchUserResources(params),
    variables: params?.variables,
  });
};

export const listResources = async (
  params?: Params<{
    id: string;
    sk: ModelStringKeyConditionInput;
    limit?: number;
    nextToken?: string;
  }>,
) => {
  const { label, fields, variables } = params || {};
  const result = await GraphQLAPI.client.query<{
    listResources: ModelResourceConnection;
  }>({
    query: queries.resource.listResources({
      label,
      fields,
    }),
    variables,
  });

  return result.data?.listResources;
};

export const listResourcesByWorkspaceId = async (
  params?: Params<{
    workspaceId: string;
    sk: ModelStringKeyConditionInput;
    limit?: number;
    nextToken?: string;
  }>,
) => {
  const { label, fields, variables } = params || {};
  const result = await GraphQLAPI.client.query<{
    listResourcesByWorkspaceId: ModelResourceConnection;
  }>({
    query: queries.resource.listResourcesByWorkspaceId({
      label,
      fields,
    }),
    variables,
  });

  return result.data?.listResourcesByWorkspaceId;
};

export const listResourcesBySkThenId = async (
  params?: Params<{
    sk: string;
    id?: ModelStringKeyConditionInput;
    limit?: number;
    nextToken?: string;
  }>,
) => {
  const { label, fields, variables } = params || {};
  const result = await GraphQLAPI.client.query<{
    listResourcesBySortKey: ModelResourceConnection;
  }>({
    query: queries.resource.listResourcesBySkThenId({
      label,
      fields,
    }),
    variables,
  });

  return result.data?.listResourcesBySortKey;
};
